import warningIcon from "$assets/icons/warning-icon.svg?url";
import { Button } from "$components/button/button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import { toast } from "$global-providers/toast-provider";
import { gql } from "@apollo/client";
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import { FormattedMessage, useIntl } from "react-intl";
import { useConnectOrganisationPriceListMutation } from "./__generated__/connect-to-org-price-list-modal.gql";

const useStyles = makeStyles((theme) => ({
  warningIcon: {
    marginRight: theme.spacing(2),
  },
}));

gql`
  mutation connectOrganisationPriceList($priceListId: ID!) {
    priceListV2 {
      connectOrganisationPriceList(input: { priceListId: $priceListId }) {
        organisationPriceListId
      }
    }
  }
`;

export interface ConnectToOrgPriceListModalProps {
  open: boolean;
  onClose: () => void;
  priceListId: string;
  onCompleted?: () => void;
}

export const ConnectToOrgPriceListModal = ({
  open,
  onClose,
  priceListId,
  onCompleted,
}: ConnectToOrgPriceListModalProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const [connectToOrgPriceList] = useConnectOrganisationPriceListMutation();

  const handleConnect = () => {
    connectToOrgPriceList({ variables: { priceListId } })
      .then(() => {
        toast.success(
          formatMessage({
            id: "pricelist.connectToPriceList.success",
            defaultMessage: "Connected to organisation price list",
          }),
        );
        onCompleted?.();
      })
      .catch((error: Error) => {
        const noOrgPriceList = error.message
          .toLowerCase()
          .includes("no price list exists for organisation");
        toast.error(
          formatMessage(
            noOrgPriceList
              ? {
                  id: "pricelist.connectToPriceList.error.noOrgPriceList",
                  defaultMessage:
                    "Your organisation doesn't have a price list to connect to yet. Please ask your administrator to set one up.",
                }
              : {
                  id: "pricelist.connectToPriceList.error.generic",
                  defaultMessage:
                    "Couldn't connect to the organisation price list. Please try again.",
                },
          ),
        );
      });
    onClose();
  };

  return (
    <DialogContainer open={open}>
      <DialogHeader title="">
        <Box display="flex" alignItems="center">
          <img
            src={warningIcon}
            alt="warning"
            className={classes.warningIcon}
          />
          <Typography variant="h5">
            <FormattedMessage
              id="pricelist.modal.conectToOrgPriceList.modal.title"
              defaultMessage="Connect to Org Price List"
            />
          </Typography>
        </Box>
      </DialogHeader>
      <DialogBody>
        <Grid container>
          <Typography>
            <FormattedMessage
              id="pricelist.modal.conectToOrgPriceList.body"
              defaultMessage="Connecting to your organisations price list will add new items to your price list. The prices for these items will be maintained by your organisation."
            />
          </Typography>
        </Grid>
      </DialogBody>
      <DialogFooter>
        <Button color="secondary" onClick={() => onClose()}>
          <FormattedMessage
            id="pricelist.modal.conectToOrgPriceList.cancel"
            defaultMessage="Cancel"
          />
        </Button>
        <Button
          onClick={handleConnect}
          data-testid="pricelist.modal.conectToOrgPriceList.accept"
        >
          <FormattedMessage
            id="pricelist.modal.conectToOrgPriceList.confirm"
            defaultMessage="Connect"
          />
        </Button>
      </DialogFooter>
    </DialogContainer>
  );
};

export default ConnectToOrgPriceListModal;
